import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import * as dataHooks from './dataHooks';
import {
  TEXT_AREA_DISABLED,
  TEXT_AREA_ERROR,
  TEXT_AREA_FOCUS,
  TEXT_AREA_SUCCESS,
  TEXT_AREA_THEME,
} from './dataKeys';
import {
  IconTooltipDriver,
  iconTooltipDriverFactory,
} from '../IconTooltip/IconTooltip.driver';
import { TextAreaTheme } from './types';
import { focusable, FocusableDriver } from '../../test/unidriver-mixins';
import { dataHook } from '../../testkitUtils/utils';

export interface TextAreaDriver extends BaseUniDriver, FocusableDriver {
  value(): Promise<string>;
  label(): Promise<string>;
  error(): Promise<boolean>;
  success(): Promise<boolean>;
  disabled(): Promise<boolean>;
  errorIcon(): Promise<boolean>;
  _errorIcon: IconTooltipDriver;
  placeholder(): Promise<string>;
  theme(): Promise<TextAreaTheme>;
  typeText(text: string): Promise<any>;
  getTextareaElement(): Promise<HTMLElement>;
  isCharCountExist(): Promise<boolean>;
  getCharCountText(): Promise<string>;
  getMaxLength(): Promise<number>;
  getLang(): Promise<string>;
  isErrorTooltipShown(): Promise<boolean>;
  clickOnErrorIcon(): Promise<void>;
  getErrorMessage(): Promise<string>;
  ariaDescribedby(): Promise<string>;
  isRequired(): Promise<string>;
  getLabelAsterisk(): UniDriver;
  isFocused(): Promise<boolean>;
}

function textAreaLabel(base: UniDriver) {
  return base.$(dataHook(dataHooks.LABEL_DATA_HOOK));
}

function textArea(base: UniDriver) {
  return base.$(dataHook(dataHooks.TEXT_AREA_DATA_HOOK));
}

function errorIcon(base: UniDriver) {
  return base.$(dataHook(dataHooks.TEXT_AREA_ERROR_DATA_HOOK));
}

async function isDisabled(base) {
  const disabled = await base.attr(TEXT_AREA_DISABLED);
  return disabled === 'true';
}

async function isError(base) {
  return (await base.attr(TEXT_AREA_ERROR)) === 'true';
}

async function isSuccess(base) {
  return (await base.attr(TEXT_AREA_SUCCESS)) === 'true';
}

async function isFocused(base) {
  return (await base.attr(TEXT_AREA_FOCUS)) === 'true';
}

function getTheme(base) {
  return base.attr(TEXT_AREA_THEME);
}

function getCharCount(base: UniDriver) {
  return base.$(dataHook(dataHooks.CHAR_COUNTER_DATA_HOOK));
}

export const textAreaDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): TextAreaDriver => {
  const iconTooltipDriver = iconTooltipDriverFactory(
    base.$(dataHook(dataHooks.ERROR_ICON_DATA_HOOK)),
    body,
  );

  function getTextareaElement() {
    return textArea(base).getNative();
  }

  return {
    ...baseUniDriverFactory(base),
    ...focusable(textArea(base)),
    async label() {
      return textAreaLabel(base).text();
    },
    async value() {
      return textArea(base).value();
    },
    async placeholder() {
      return textArea(base).attr('placeholder');
    },
    async ariaDescribedby() {
      return textArea(base).attr('aria-describedby');
    },
    async typeText(text: string) {
      return textArea(base).enterValue(text);
    },
    async disabled() {
      return isDisabled(base);
    },
    async theme(): Promise<TextAreaTheme> {
      return getTheme(base) as Promise<TextAreaTheme>;
    },
    async errorIcon() {
      return errorIcon(base).exists();
    },
    _errorIcon: iconTooltipDriver,
    async error() {
      return isError(base);
    },
    async success() {
      return isSuccess(base);
    },
    getTextareaElement,
    async isCharCountExist() {
      return getCharCount(base).exists();
    },
    async getCharCountText() {
      return getCharCount(base).text();
    },
    async getMaxLength() {
      return parseInt(await textArea(base).attr('maxlength'), 10);
    },
    async getLang() {
      return base.attr('lang');
    },
    async getErrorMessage() {
      const ariaDescribedByArr = (
        await textArea(base).attr('aria-describedby')
      ).split(' ');
      const errorAriaDescribedBy = ariaDescribedByArr.find(
        (val) => val.indexOf('error-message') > -1,
      );
      const tooltipContent = document.querySelector(`#${errorAriaDescribedBy}`);

      return tooltipContent ? tooltipContent.textContent : '';
    },

    /**
     * Is error tooltip shown
     * @return boolean
     */
    async isErrorTooltipShown() {
      return iconTooltipDriver.tooltipExists();
    },

    /**
     * Click on error icon
     * @return void
     */
    async clickOnErrorIcon() {
      await iconTooltipDriver.click();
    },
    async isRequired() {
      return (await getTextareaElement()).required;
    },
    async isFocused() {
      return isFocused(base);
    },
    getLabelAsterisk() {
      return base.$(dataHook(dataHooks.LABEL_ASTERISK_DATA_HOOK));
    },
  };
};
